import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, CircularProgress, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Search, Close, Restaurant, ViewList, ViewModule, Refresh
} from '@mui/icons-material';

import { foodAPI } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const CATEGORIES = ['All', 'Protein', 'Carbs', 'Fats', 'Dairy', 'Vegetables', 'Legumes', 'Supplements', 'Other'];
const DIETARY    = ['all', 'vegan', 'veg', 'egg', 'non_veg'];

const DIETARY_LABELS = { all: 'All', vegan: 'Vegan', veg: 'Vegetarian', egg: 'Egg', non_veg: 'Non-Veg' };
const DIETARY_COLORS = { vegan: 'success', veg: 'success', egg: 'warning', non_veg: 'error' };

const CAT_COLORS = {
  Protein: '#6C63FF', Carbs: '#FFA040', Fats: '#FF6B6B',
  Dairy: '#26C6DA', Vegetables: '#4CAF82', Legumes: '#8BC34A',
  Supplements: '#AB47BC', Other: '#90A4AE',
};

const emptyItem = () => ({
  name: '', category: 'Protein', dietaryType: 'vegan',
  cal100: '', protein100: '', carbs100: '', fat100: '', fiber100: '',
  defaultServing: 100,
});

export default function FoodItems() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [search, setSearch]       = useState('');
  const [category, setCategory]   = useState('All');
  const [dietary, setDietary]     = useState('all');
  const [viewMode, setViewMode]   = useState('table');
  const [formOpen, setFormOpen]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [page, setPage]   = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await foodAPI.getAll({ search, category, dietary, page, limit });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load food items');
    } finally {
      setLoading(false);
    }
  }, [search, category, dietary, page, limit]);

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, category, dietary]);

  const handleSave = async (data) => {
    try {
      if (data._id) {
        await foodAPI.update(data._id, data);
        setSuccess('Food item updated');
      } else {
        await foodAPI.create(data);
        setSuccess('Food item created');
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      throw e;
    }
  };

  const openAdd  = () => { setEditing(emptyItem()); setFormOpen(true); };
  const openEdit = (item) => { setEditing({ ...emptyItem(), ...item }); setFormOpen(true); };

  // Group by category for grid view
  const grouped = items.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Food Database"
        subtitle="Manage food items used in meal plans and diet logging."
        gradient="linear-gradient(90deg, #D97706, #FBBF24)"
        actions={<>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd}>Add Food Item</Button>
        </>}
      />


      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search food items…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
          />

          {/* Category */}
          <TextField
            select size="small" label="Category"
            value={category} onChange={e => setCategory(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>

          {/* Dietary */}
          <TextField
            select size="small" label="Dietary"
            value={dietary} onChange={e => setDietary(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {DIETARY.map(d => <MenuItem key={d} value={d}>{DIETARY_LABELS[d]}</MenuItem>)}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          {/* Count */}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {items.length} items
          </Typography>

          {/* View toggle */}
          <ToggleButtonGroup
            size="small" value={viewMode} exclusive
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="table"><ViewList fontSize="small" /></ToggleButton>
            <ToggleButton value="grid"><ViewModule fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* ── Content ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Restaurant sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="body1" fontWeight={600} gutterBottom>No food items found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add food items or seed the database from Database Management.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add First Item</Button>
        </Box>
      ) : viewMode === 'table' ? (
        <TableView items={items} onEdit={openEdit} />
      ) : (
        <GridView grouped={grouped} onEdit={openEdit} />
      )}

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={setPage} onLimit={v => { setLimit(v); setPage(1); }} />

      {/* ── Form Dialog ── */}
      {formOpen && editing && (
        <FoodItemDialog
          item={editing}
          onSave={handleSave}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onError={setError}
        />
      )}
    </Box>
  );
}

// ── Table View ─────────────────────────────────────────────────────────────────
function TableView({ items, onEdit }) {
  return (
    <Paper elevation={0} sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8FAFC', borderBottom: '2px solid', borderColor: 'divider' }}>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Dietary</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Cal/100g</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Protein</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Carbs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Fat</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Fiber</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Serving</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={item._id} hover sx={{ '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F9FAFB' } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>{item.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.category}
                    size="small"
                    variant="outlined"
                    sx={{
                      bgcolor: `${CAT_COLORS[item.category] || '#90A4AE'}12`,
                      color: CAT_COLORS[item.category] || '#90A4AE',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      borderColor: `${CAT_COLORS[item.category] || '#90A4AE'}40`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={DIETARY_LABELS[item.dietaryType] || item.dietaryType}
                    size="small"
                    variant="outlined"
                    sx={{
                      bgcolor: DIETARY_COLORS[item.dietaryType] === 'success' ? 'rgba(34, 197, 94, 0.1)' : DIETARY_COLORS[item.dietaryType] === 'warning' ? 'rgba(217, 119, 6, 0.1)' : DIETARY_COLORS[item.dietaryType] === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                      color: DIETARY_COLORS[item.dietaryType] === 'success' ? '#22C55E' : DIETARY_COLORS[item.dietaryType] === 'warning' ? '#D97706' : DIETARY_COLORS[item.dietaryType] === 'error' ? '#EF4444' : 'text.primary',
                      borderColor: DIETARY_COLORS[item.dietaryType] === 'success' ? '#86EFAC' : DIETARY_COLORS[item.dietaryType] === 'warning' ? '#FCD34D' : DIETARY_COLORS[item.dietaryType] === 'error' ? '#FECACA' : 'divider',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                    }}
                  />
                </TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>{item.cal100}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: 'primary.main' }}>{item.protein100}g</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: 'warning.main' }}>{item.carbs100}g</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: 'error.main' }}>{item.fat100}g</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.fiber100 || 0}g</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{item.defaultServing}g</Typography></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(item)} sx={{ color: 'primary.main', '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.1)' : '#EFF6FF' } }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ── Grid View ──────────────────────────────────────────────────────────────────
function GridView({ grouped, onEdit }) {
  return (
    <Stack spacing={4}>
      {Object.entries(grouped).map(([cat, items]) => (
        <Box key={cat}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2.5,
              pb: 1.5,
              borderBottom: 2,
              borderColor: CAT_COLORS[cat] || '#90A4AE',
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: CAT_COLORS[cat] || '#90A4AE',
                boxShadow: `0 0 0 3px ${(CAT_COLORS[cat] || '#90A4AE')}20`,
              }}
            />
            <Typography variant="h6" fontWeight={800} sx={{ fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: CAT_COLORS[cat] || '#90A4AE' }}>
              {cat} <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>({items.length})</Typography>
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {items.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1.5px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      borderColor: CAT_COLORS[cat] || 'primary.main',
                      boxShadow: `0 8px 24px ${(CAT_COLORS[cat] || 'primary.main')}15`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, pb: 1.5 }}>
                    {/* Header: Name + Dietary */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                          fontSize: '0.95rem',
                          color: 'text.primary',
                          flex: 1,
                          lineHeight: 1.3,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Chip
                        label={DIETARY_LABELS[item.dietaryType] || item.dietaryType}
                        size="small"
                        variant="outlined"
                        sx={{
                          bgcolor: DIETARY_COLORS[item.dietaryType] === 'success' ? 'rgba(34, 197, 94, 0.1)' : DIETARY_COLORS[item.dietaryType] === 'warning' ? 'rgba(217, 119, 6, 0.1)' : DIETARY_COLORS[item.dietaryType] === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                          color: DIETARY_COLORS[item.dietaryType] === 'success' ? '#22C55E' : DIETARY_COLORS[item.dietaryType] === 'warning' ? '#D97706' : DIETARY_COLORS[item.dietaryType] === 'error' ? '#EF4444' : 'text.primary',
                          borderColor: DIETARY_COLORS[item.dietaryType] === 'success' ? '#86EFAC' : DIETARY_COLORS[item.dietaryType] === 'warning' ? '#FCD34D' : DIETARY_COLORS[item.dietaryType] === 'error' ? '#FECACA' : 'divider',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 24,
                          flexShrink: 0,
                        }}
                      />
                    </Stack>

                    {/* Macros Grid */}
                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem', mb: 0.25 }}>
                            CALORIES
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'text.primary' }}>
                            {item.cal100}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}>
                            kcal
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.1)' : '#EFF6FF',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: '#BFDBFE',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 600, fontSize: '0.7rem', mb: 0.25 }}>
                            PROTEIN
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'primary.main' }}>
                            {item.protein100}g
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(217, 119, 6, 0.1)' : '#FFFBEB',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: '#FCD34D',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 600, fontSize: '0.7rem', mb: 0.25 }}>
                            CARBS
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'warning.main' }}>
                            {item.carbs100}g
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: '#FECACA',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 600, fontSize: '0.7rem', mb: 0.25 }}>
                            FAT
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'error.main' }}>
                            {item.fat100}g
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Meta info */}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                      Per 100g · Serving: <Typography component="span" fontWeight={700}>{item.defaultServing}g</Typography>
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ pt: 0, px: 2, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Edit />}
                      onClick={() => onEdit(item)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        flex: 1,
                      }}
                    >
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}

// ── Food Item Form Dialog ──────────────────────────────────────────────────────

const PORTION_UNITS = [
  { value: 'grams',      label: 'Grams (g)',          grams: null   }, // user enters exact grams
  { value: 'small_bowl', label: 'Small Bowl (~200g)',  grams: 200    },
  { value: 'large_bowl', label: 'Large Bowl (~400g)',  grams: 400    },
  { value: 'count',      label: 'Count (pieces)',      grams: null   }, // user enters qty, admin sets g/piece
];

function FoodItemDialog({ item: initialItem, onSave, onClose, onError }) {
  const [item, setItem]   = useState({ ...initialItem });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Portion size state
  const [portionUnit, setPortionUnit]   = useState('grams');
  const [portionQty, setPortionQty]     = useState(100);   // grams value OR piece count
  const [gramsPerPiece, setGramsPerPiece] = useState(50);  // only used for 'count'

  // Compute the effective defaultServing in grams
  const effectiveServing = () => {
    if (portionUnit === 'grams')      return portionQty;
    if (portionUnit === 'small_bowl') return 200;
    if (portionUnit === 'large_bowl') return 400;
    if (portionUnit === 'count')      return portionQty * gramsPerPiece;
    return 100;
  };

  const update = (field, value) => setItem(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!item.name?.trim())  { setError('Name is required'); return; }
    if (!item.category)      { setError('Category is required'); return; }
    if (item.cal100 === '')  { setError('Calories per 100g is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...item,
        cal100:         Number(item.cal100),
        protein100:     Number(item.protein100),
        carbs100:       Number(item.carbs100),
        fat100:         Number(item.fat100),
        fiber100:       Number(item.fiber100 || 0),
        defaultServing: effectiveServing(),
      };
      await onSave(payload);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to save';
      setError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  const unitInfo = PORTION_UNITS.find(u => u.value === portionUnit);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{item._id ? 'Edit Food Item' : 'Add Food Item'}</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12}>
            <TextField
              label="Food Name *" value={item.name}
              onChange={e => update('name', e.target.value)} fullWidth
            />
          </Grid>

          {/* Category + Dietary */}
          <Grid item xs={6}>
            <TextField select label="Category *" value={item.category} onChange={e => update('category', e.target.value)} fullWidth>
              {CATEGORIES.filter(c => c !== 'All').map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Dietary Type" value={item.dietaryType} onChange={e => update('dietaryType', e.target.value)} fullWidth>
              {DIETARY.filter(d => d !== 'all').map(d => <MenuItem key={d} value={d}>{DIETARY_LABELS[d]}</MenuItem>)}
            </TextField>
          </Grid>

          {/* Nutrition per 100g */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              NUTRITION PER 100g
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Calories (kcal) *" type="number" value={item.cal100}
              onChange={e => update('cal100', e.target.value)} fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Protein (g)" type="number" value={item.protein100}
              onChange={e => update('protein100', e.target.value)} fullWidth
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Carbs (g)" type="number" value={item.carbs100}
              onChange={e => update('carbs100', e.target.value)} fullWidth
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Fat (g)" type="number" value={item.fat100}
              onChange={e => update('fat100', e.target.value)} fullWidth
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Fiber (g)" type="number" value={item.fiber100}
              onChange={e => update('fiber100', e.target.value)} fullWidth
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>

          {/* Portion size section */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              DEFAULT PORTION SIZE
            </Typography>
          </Grid>

          {/* Unit selector */}
          <Grid item xs={12}>
            <TextField
              select
              label="Portion Unit"
              value={portionUnit}
              onChange={e => {
                setPortionUnit(e.target.value);
                // Reset qty to sensible defaults
                if (e.target.value === 'grams')      setPortionQty(100);
                if (e.target.value === 'small_bowl') setPortionQty(1);
                if (e.target.value === 'large_bowl') setPortionQty(1);
                if (e.target.value === 'count')      setPortionQty(1);
              }}
              fullWidth
              size="small"
            >
              {PORTION_UNITS.map(u => (
                <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Grams: show weight input */}
          {portionUnit === 'grams' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Amount (grams)"
                type="number"
                value={portionQty}
                onChange={e => setPortionQty(Number(e.target.value))}
                fullWidth size="small"
                inputProps={{ min: 1, step: 1 }}
                InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
              />
            </Grid>
          )}

          {/* Small / Large bowl: fixed grams, just show info */}
          {(portionUnit === 'small_bowl' || portionUnit === 'large_bowl') && (
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Fixed serving: <strong>{unitInfo.grams}g</strong> per {portionUnit === 'small_bowl' ? 'small bowl' : 'large bowl'}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Count: qty + grams per piece */}
          {portionUnit === 'count' && (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Default Quantity"
                  type="number"
                  value={portionQty}
                  onChange={e => setPortionQty(Number(e.target.value))}
                  fullWidth size="small"
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">pcs</InputAdornment> }}
                  helperText="Number of pieces"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Grams per Piece"
                  type="number"
                  value={gramsPerPiece}
                  onChange={e => setGramsPerPiece(Number(e.target.value))}
                  fullWidth size="small"
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                  helperText="Weight of 1 piece"
                />
              </Grid>
            </>
          )}

          {/* Effective serving preview */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.100' }}>
              <Typography variant="body2">
                <strong>Effective serving:</strong> {effectiveServing()}g
                {item.cal100 && (
                  <span style={{ marginLeft: 12, color: '#666' }}>
                    ≈ {Math.round(Number(item.cal100) * effectiveServing() / 100)} kcal per serving
                  </span>
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : item._id ? 'Update' : 'Add Food Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
